import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Typography } from '@mui/material';
import { ArrowBackIcon as ArrowBack, SaveIcon } from 'components/common/Icon';
import {
  FormationsTertiaryButton,
  FormationsGhostButton,
} from 'components/common/Buttons';
import { queryClient } from 'states/reactQueryClient';
import { ReadOnlyForm } from 'components/common/ReadOnlyForm';
import {
  BUSINESS_DESCRIPTION_LIMIT,
  companyDetails1120OnlyStep1Schema,
  companyDetailsCustomerStep2,
} from 'schemas/companyDetailsSchema';
import {
  FormationsFormActions,
  FormationsFormContainer,
  FormationsFormContent,
  FormationsFormData,
  FormationsFormFields,
  FormationsFormItem,
  IFormField,
  renderField,
} from 'components/common/FormationsForm2';
import { UIDateFormat } from 'helpers/dateTimeFormat';
import {
  IAccount,
  ProgressTrackerGroupRequest,
  ProgressTrackerGroups,
  ProgressTrackerStages,
  ProgressTrackerStatus,
} from 'services/account';
import { Company } from 'models/company';
import { ENTITY_MAPPING } from 'constants/common';
import {
  useUpdateCompany,
  useIndustryOptions,
  useUpdateAccountProgress,
} from 'hooks/api';
import { EinFieldTitle } from 'components/ProgressTracker/Incorporation/EinFieldTitle';
import { formatNumberWithDashes } from 'helpers/text-transformer';
import { formatEIN } from 'helpers/progressTracker';
import { findIndustryTitleFromCode } from 'components/IndustryOptions/helpers';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { formatEinInput } from 'components/common/FormationsForm2/FormationsTextField';
import { getProgressTrackerStageStatus } from 'components/ProgressTracker/Incorporation/state';
import { useCreateSupportTicket } from 'hooks/useTickets';
import { showToastOnError } from 'components/toast/showToast';
import { SubtitleText } from 'components/ProgressTracker/StyledTypography';
import { StepInputContentContainer } from '../Components/StepInputContentContainer';
import { STATES } from '../../../enums';

interface Step1FormProps {
  companyData: Company;
  onSubmit: (data: FormationsFormData) => Promise<void>;
}
const Step1Form = ({ onSubmit, companyData }: Step1FormProps) => {
  const [disabled, setDisabled] = useState<boolean>(false);
  const step1Fields: IFormField[] = [
    {
      type: FormationsFormFields.Text,
      name: 'name',
      label: 'Company Name',
      options: {
        placeholder: 'Company Name',
        autoFocus: true,
        maxLength: 80,
      },
    },
    {
      type: FormationsFormFields.DatePicker,
      name: 'incorporationDate',
      label: 'Date of Incorporation',
      options: {
        placeholder: 'MM/DD/YYYY',
      },
    },
    {
      type: FormationsFormFields.Text,
      name: 'ein',
      label: 'Employer Identification Number (EIN)',
      disabled,
      options: {
        maxLength: 10,
        placeholder: '12-3456789',
        formatInput: formatEinInput,
      },
    },
    {
      type: FormationsFormFields.CheckboxField,
      name: 'noEin',
      label: 'I can’t find this information',
    },
  ];
  const fields: IFormField[] = step1Fields;

  const onChange = (data: FormationsFormData) => {
    setDisabled(data.hasEinNo);
  };

  const defaultNoEin = useMemo(() => {
    if (companyData?.ein != null) {
      return false;
    }
    if (companyData?.hasEinNo) {
      return true;
    }
    return false;
  }, [companyData?.ein, companyData?.hasEinNo]);

  const formInstance = useForm({
    defaultValues: {
      name: companyData?.name,
      incorporationDate: companyData?.incorporationDate ?? null,
      ein: companyData?.ein ? formatEIN(companyData?.ein) : companyData?.ein,
      noEin: defaultNoEin,
    },
    resolver: yupResolver(companyDetails1120OnlyStep1Schema),
  })
  const {
    handleSubmit,
    formState: { isSubmitting },
    getValues,
    watch,
  } = formInstance;

  useEffect(() => {
    onChange(getValues());
  }, [watch()]);

  return (
    <FormationsFormContainer>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormationsFormContent>
          <StepInputContentContainer>
            <Grid container spacing={2} marginLeft={0.25}>
              {fields.map((field) => (
                <Grid item key={field.name} xs={12} sx={{ mb: 1 }}>
                  {renderField(field, formInstance)}
                </Grid>
              ))}
            </Grid>
          </StepInputContentContainer>
        </FormationsFormContent>
        <FormationsFormActions>
          <FormationsTertiaryButton
            isLoading={isSubmitting}
            type="submit"
            size="large"
            data-testid="save-step-1"
            loadingPosition="end"
            endIcon={<SaveIcon />}
          >
            Save and Continue
          </FormationsTertiaryButton>
        </FormationsFormActions>
      </form>
    </FormationsFormContainer>
  );
};

interface Step2FormProps {
  companyData: Company;
  onSubmit: (data: FormationsFormData) => Promise<void>;
  onBackClick: () => void;
}
const mapState = (state: { name: string; code: string }) => ({
  name: state.name,
  value: state.code,
});
const Step2Form = ({ companyData, onSubmit, onBackClick }: Step2FormProps) => {
  const { data: industryOptionsData } = useIndustryOptions();
  const isScorp = companyData.entityType === 'S-CORP';
  const isSoleProp = companyData.entityType === ENTITY_MAPPING.sole_prop;
  const fields = [
    {
      type: FormationsFormFields.AutoComplete,
      name: 'stateOfIncorporation',
      label: 'State of Incorporation',
      options: {
        placeholder: 'Washington',
        options: STATES.map(mapState),
      },
    },
    {
      type: FormationsFormFields.AutoComplete,
      name: 'industry',
      label: 'Business Industry',
      options: {
        placeholder: '',
        options: industryOptionsData?.map(({ title, code }) => ({
          name: title,
          value: code,
        })),
      },
    },
    {
      type: FormationsFormFields.TextArea,
      name: 'description',
      label: 'Business Description',
      options: {
        placeholder: 'Description for your business',
        characterLimit: BUSINESS_DESCRIPTION_LIMIT,
      },
    },
  ];
  const formInstance = useForm({
    defaultValues: {
      stateOfIncorporation: STATES.find(
        (item) => item.code === companyData.stateOfIncorporation,
      )?.code,
      industry: industryOptionsData?.find(
        (item) => item.code === companyData.industry,
      )?.code,
      description: companyData.description,
    },
    resolver: yupResolver(companyDetailsCustomerStep2),
    context: {
      isSoleProp,
      isScorp,
    },
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = formInstance;

  return (
    <FormationsFormContainer>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormationsFormContent>
          <StepInputContentContainer>
            <Grid container spacing={2} marginLeft={0.25}>
              {fields.map((field) => (
                <FormationsFormItem key={field.name}>
                  {renderField(field, formInstance)}
                </FormationsFormItem>
              ))}
            </Grid>
          </StepInputContentContainer>
        </FormationsFormContent>
        <FormationsFormActions>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <FormationsGhostButton
              type="button"
              size="large"
              startIcon={<ArrowBack />}
              onClick={onBackClick}
              disabled={isSubmitting}
            >
              Back
            </FormationsGhostButton>
            <FormationsTertiaryButton
              isLoading={isSubmitting}
              type="submit"
              size="large"
              data-testid="save-step-2"
              loadingPosition="end"
              endIcon={<SaveIcon />}
            >
              Save and Continue
            </FormationsTertiaryButton>
          </Box>
        </FormationsFormActions>
      </form>
    </FormationsFormContainer>
  );
};

interface TParams {
  handleComplete: (status: ProgressTrackerStatus) => void;
  companyData: Company;
  status: ProgressTrackerStatus | null;
  account: IAccount;
}

export const shouldShowReadOnly = (
  companyData: Company,
  status: ProgressTrackerStatus | null,
): boolean => {
  if (status === ProgressTrackerStatus.Completed) {
    return true;
  }
  const fields = [
    companyData.name,
    companyData.incorporationDate,
    companyData.stateOfIncorporation,
    companyData.industry,
    companyData.description,
  ];
  const allFilled = fields.every((i) => i != null);
  if (!allFilled) {
    return false;
  }
  if (status === ProgressTrackerStatus.Started && companyData.ein == null) {
    return true;
  }
  return false;
};

export const CompanyDetails = ({
  handleComplete,
  companyData,
  status,
  account,
}: TParams) => {
  const [formStep, setFormStep] = useState(0);
  const { t } = useTranslation();
  const isSoleProp = companyData?.entityType === ENTITY_MAPPING.sole_prop;
  const { data: industryOptionsData } = useIndustryOptions();

  const { mutateAsync: updateCompany } = useUpdateCompany({
    onSuccess: () =>
      queryClient.invalidateQueries(['getCompany', companyData?.id]),
  });
  const { createSupportTicketAsync } = useCreateSupportTicket(account.id);

  const onStep1Submit = async (formData: Company & { noEin: boolean }) => {
    const companyDetails = {
      name: formData?.name ?? undefined,
      incorporationDate: formData?.incorporationDate
        ? UIDateFormat(formData?.incorporationDate)
        : undefined,
      ein: formData.ein ? formatNumberWithDashes(formData.ein) : undefined,
      hasEinNo: !formData.noEin,
    };
    if (formData?.noEin) {
      companyDetails.ein = undefined;
    }

    if (companyData?.id) {
      try {
        await createSupportTicketAsync('no-ein');
        await updateCompany({ id: companyData.id, data: companyDetails });
        setFormStep(1);
        if (status === null) {
          handleComplete(ProgressTrackerStatus.Started);
        }
      } catch (err) {
        showToastOnError(err);
      }
    }
  };

  const onStep2Submit = async (formData: Company) => {
    const companyDetails = {
      industry: formData.industry,
      stateOfIncorporation: formData.stateOfIncorporation,
      description: formData.description,
    };
    if (companyData?.id) {
      await updateCompany({ id: companyData.id, data: companyDetails });
      if (ProgressTrackerStatus.Completed !== status) {
        handleComplete(ProgressTrackerStatus.Completed);
      }
    }
  };

  const onUpdateEin = () => {
    handleComplete(ProgressTrackerStatus.Completed);
  };

  const subHeading = isSoleProp
    ? t('progressTracker.incorporation.companyDetailSubHeadingSoleProp')
    : t('progressTracker.incorporation.companyDetailSubHeading');

  return (
    <div>
      <Typography variant="h5B" component="h5" data-test-id="company-details">
        Company Details
      </Typography>
      {shouldShowReadOnly(companyData, status) ? (
        <ReadOnlyForm
          items={[
            {
              key: 'company-name',
              title: 'Company Name',
              value: companyData?.name ?? '',
            },
            {
              key: 'date-of-incorporation',
              title: 'Date of Incorporation',
              value: companyData?.incorporationDate,
            },
            {
              key: 'Employer-Identification-Number-(EIN)',
              title:
                companyData.ein == null ? (
                  <EinFieldTitle
                    companyData={companyData}
                    label="Employer Identification Number (EIN)"
                    onSubmitted={onUpdateEin}
                  />
                ) : (
                  'Employer Identification Number (EIN)'
                ),
              value: companyData?.ein ? formatEIN(companyData?.ein) : undefined,
            },
            {
              key: 'State-of-Incorporation',
              title: 'State of Incorporation',
              value: companyData?.stateOfIncorporation,
            },
            {
              key: 'Business-Industry',
              title: 'Business Industry',
              value: findIndustryTitleFromCode(
                industryOptionsData ?? [],
                companyData?.industry,
              ),
            },
            {
              key: 'Business-Description',
              title: 'Business Description',
              value: companyData?.description,
            },
          ]}
        />
      ) : (
        <>
          <Box
            sx={{
              display: formStep === 0 ? 'block' : 'none',
            }}
          >
            <SubtitleText variant="body1">{subHeading}</SubtitleText>
            <Step1Form onSubmit={onStep1Submit} companyData={companyData} />
          </Box>
          <Box
            sx={{
              display: formStep === 1 ? 'block' : 'none',
            }}
          >
            <SubtitleText variant="body1">
              {t('progressTracker.incorporation.whatBusinessIndustryYouAreIn')}
            </SubtitleText>
            <Step2Form
              companyData={companyData}
              onSubmit={onStep2Submit}
              onBackClick={() => {
                setFormStep(0);
              }}
            />
          </Box>
        </>
      )}
    </div>
  );
};

interface ContainerProps {
  company: Company;
  account: IAccount;
}

export const CompanyDetails1120sOnlyContainer = ({
  account,
  company,
}: ContainerProps) => {
  const { mutateAsync: updateAccountProgress } = useUpdateAccountProgress(
    account?.id,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['progressTracker']);
      },
    },
  );
  const handleComplete = async (status: ProgressTrackerStatus) => {
    const requestProps: ProgressTrackerGroupRequest = {
      group: ProgressTrackerGroups.Incorporation,
      status,
      stage: ProgressTrackerStages.CompanyDetails,
    };

    await updateAccountProgress({
      progress: [requestProps],
      eventData: {
        stage: ProgressTrackerStages.CompanyDetails,
        stageStatus: status,
        entityType: company?.entityType ?? company?.legacyEntityType ?? '',
      },
    });
  };

  const status = getProgressTrackerStageStatus(
    account ?? null,
    ProgressTrackerStages.CompanyDetails,
  );

  return (
    <CompanyDetails
      handleComplete={handleComplete}
      status={status ?? null}
      companyData={company}
      account={account}
    />
  );
};
